import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";

import api from "../../Utils/api";

import CopyResultDialog from "./CopyResultDialog";

// Static template data for Load Template modal

export default function CopyDropdown({
  selectedDate,
  period,
  getSchedules,
  schedules,
}) {
  const [open, setOpen] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showLoadTemplateModal, setShowLoadTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [searchTemplate, setSearchTemplate] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [showCopyModal, setShowCopyModal] = useState(false); // Add this state
  const [copyOption, setCopyOption] = useState("next"); // Add this state

  const [copyResults, setCopyResults] = useState([]);

  const [openCopyResultDialog, setOpenCopyResultDialog] = useState(false);

  const [conflictSchedules, setConflictSchedules] = useState([]);
  const [showConflict, setShowConflict] = useState(false);

  const [templateList, setTemplateList] = useState([]);

  const menuRef = useRef();

  useEffect(() => {
    getTemplateList();
  }, []);

  const handleCopySchedule = async () => {
    // Implement your copy logic here based on copyOption
    // console.log("Copy option selected:", copyOption);

    if (copyOption == "next") {
      copyToNextWeek();
    } else {
      copyFromPrevWeek();
    }

    setShowCopyModal(false);
    setCopyOption("next"); // Reset to default
  };

  // Parse date range for display
  const getDateRangeText = (selectedDate, period) => {
    if (!selectedDate || !period) return "selected dates";

    const start = new Date(selectedDate);
    const end = new Date(start);
    end.setDate(start.getDate() + period - 1); // period includes the start day

    const format = (date) =>
      date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
      });

    return `${format(start)} - ${format(end)}`;
  };

  // Usage in your component:
  const dateRangeText = getDateRangeText(selectedDate, period);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter templates based on search
  const filteredTemplates = templateList.filter(
    (template) =>
      template.title.toLowerCase().includes(searchTemplate.toLowerCase()) ||
      template.desc.toLowerCase().includes(searchTemplate.toLowerCase())
  );

  // Save as Template handler (static, no API)
  const handleSaveTemplate = async () => {
    const uniqueUserIds = new Set(schedules.map((sch) => sch.userID._id));
    const usersLength = uniqueUserIds.size;

    const res = await api.post("/schedule/save_template", {
      startTime: selectedDate,
      period: period,
      title: templateName,
      desc: templateDescription,
      shiftsNum: schedules.length,
      usersNum: usersLength,
      shiftsData: schedules,
    });

    if (res.data.success) {
      toast.success(res.data.message);
      getTemplateList();
    } else {
      toast.error(res.data.message);
    }

    // Simulate saving and close modal
    setShowTemplateModal(false);
    setTemplateName("");
    setTemplateDescription("");
    // Optionally show a notification
  };

  const getTemplateList = async () => {
    const res = await api.get("/schedule/get_template_list");

    if (res.data.success) {
      setTemplateList(res.data.templateList);
    }
  };

  const loadTemplate = async () => {
    const res = await api.post("/schedule/load_template", {
      startTime: selectedDate,
      period: period,
      templateID: selectedTemplate._id,
    });

    if (res.data.success) {
      toast.success(res.data.message);
      setConflictSchedules(res.data.conflictSchedules);
      if (res.data.conflicted > 0) setShowConflict(true);
      setSelectedTemplate(null);

      getSchedules();
    } else {
      toast.error(res.data.message);
    }
  };

  const copyToNextWeek = async () => {
    const res = await api.post("/schedule/copy_to_next", {
      schedules: schedules,
      period: period,
    });
    if (res.data.success) {
      toast.success(res.data.message);
      setCopyResults(res.data);
      setOpenCopyResultDialog(true);
    } else {
      toast.error("Failed to copy!");
    }
  };

  const copyFromPrevWeek = async () => {
    const res = await api.post("/schedule/copy_from_prev", {
      startTime: selectedDate,
      period: period,
    });
    if (res.data.success) {
      toast.success(res.data.message);
      setCopyResults(res.data);
      getSchedules();
      setOpenCopyResultDialog(true);
    } else {
      toast.error("Failed to copy!");
    }
  };

  return (
    <div
      className="relative inline-block md:hidden lg:block text-left"
      ref={menuRef}
    >
      <button
        type="button"
        className="flex items-center gap-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md border border-transparent hover:bg-gray-200 transition focus:outline-none"
        onClick={() => setOpen((prev) => !prev)}
      >
        <i className="fa fa-copy"></i>
        <span className="font-semibold text-sm">Copy</span>
        <i className="fa fa-caret-down w-4 h-4 ml-1"></i>
      </button>
      {open && (
        <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-2">
          <button
            onClick={() => {
              setShowCopyModal(true);
              setOpen(false);
            }}
            className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 transition"
          >
            Copy schedule
          </button>
          <div className="my-1 border-t border-gray-200" />
          <button
            className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 transition"
            onClick={() => {
              setShowTemplateModal(true);
              setOpen(false);
            }}
          >
            Save as Template
          </button>
          <button
            className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 transition"
            onClick={() => {
              setShowLoadTemplateModal(true);
              setOpen(false);
            }}
          >
            Load Template
          </button>
        </div>
      )}

      {/* Add Copy Shifts Modal */}
      {showCopyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Copy shifts
              </h2>
              <button
                onClick={() => setShowCopyModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Dates
                </h3>

                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="copyOption"
                      value="next"
                      checked={copyOption === "next"}
                      onChange={(e) => setCopyOption(e.target.value)}
                      className="w-4 h-4 text-gray-600 border-gray-300 focus:ring-gray-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      Copy to next{" "}
                      {period == 7
                        ? "week"
                        : period == 14
                        ? "2 weeks"
                        : "month"}{" "}
                      period
                    </span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="copyOption"
                      value="previous"
                      checked={copyOption === "previous"}
                      onChange={(e) => setCopyOption(e.target.value)}
                      className="w-4 h-4 text-gray-600 border-gray-300 focus:ring-gray-500"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      Copy from previous{" "}
                      {period == 7
                        ? "week"
                        : period == 14
                        ? "2 weeks"
                        : "month"}{" "}
                      period
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end px-6 py-4 border-t border-gray-200">
              <button
                onClick={handleCopySchedule}
                className="px-6 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save as Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Save Shift Template</h2>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fa fa-close w-5 h-5"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                You are about to save the shifts between {dateRangeText} as
                template.
              </p>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Name<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                  required
                />
              </div>

              <p className="text-xs text-gray-500">
                Saving template with the same name will overwrite the previous
                template.
              </p>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Description (Optional)
                </label>
                <textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                  rows="4"
                ></textarea>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={!templateName}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  templateName
                    ? "bg-gray-600 hover:bg-gray-700"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Load Template Modal */}
      {showLoadTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Choose Template</h2>
              <button
                onClick={() => setShowLoadTemplateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fa fa-close w-5 h-5"></i>
              </button>
            </div>

            {/* Search Box */}
            <div className="p-4">
              <div className="relative mb-4">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <i className="fa fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  value={searchTemplate}
                  onChange={(e) => setSearchTemplate(e.target.value)}
                  placeholder="Search Template"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                />
              </div>

              {/* Template List */}
              <div className="max-h-64 overflow-y-auto">
                {filteredTemplates.length > 0 ? (
                  filteredTemplates.map((template) => (
                    <div
                      key={template._id}
                      onClick={() => setSelectedTemplate(template)}
                      className={`p-4 mb-2 border rounded-md cursor-pointer transition ${
                        selectedTemplate?._id === template._id
                          ? "border-gray-500 bg-gray-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium">{template.title}</h3>
                          <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                            <span className="flex items-center">
                              <i className="fa fa-calendar mr-1"></i>
                              {template.shiftsNum}
                            </span>
                            <span className="flex items-center">
                              <i className="fa fa-user mr-1"></i>
                              {template.usersNum}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {template.desc}
                          </p>
                        </div>
                        <div className="text-xs text-gray-500 whitespace-nowrap ml-4">
                          <i className="fa fa-calendar mr-1"></i>
                          {template.startTime && template.period
                            ? (() => {
                                const startDate = new Date(template.startTime);
                                // period is in days, so subtract 1 to include the start date in the range
                                const endDate = new Date(startDate);
                                endDate.setDate(
                                  startDate.getDate() + template.period - 1
                                );

                                // Get weekday names
                                const startWeekday =
                                  startDate.toLocaleDateString("en-GB", {
                                    weekday: "short",
                                  });
                                const endWeekday = endDate.toLocaleDateString(
                                  "en-GB",
                                  { weekday: "short" }
                                );

                                // Format dates
                                const startStr = startDate.toLocaleDateString(
                                  "en-GB",
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "2-digit",
                                  }
                                );
                                const endStr = endDate.toLocaleDateString(
                                  "en-GB",
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "2-digit",
                                  }
                                );

                                return (
                                  <>
                                    {startWeekday} {startStr} - {endWeekday}{" "}
                                    {endStr}
                                  </>
                                );
                              })()
                            : "Mon 19/05/25 - Sun 01/06/25"}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    No templates found
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-between px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setShowLoadTemplateModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
              >
                Cancel
              </button>
              <button
                disabled={!selectedTemplate}
                onClick={loadTemplate}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  selectedTemplate
                    ? "bg-gray-600 hover:bg-gray-700"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                Load Template
              </button>
            </div>
          </div>
        </div>
      )}
      {showConflict && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                Conflicting Shifts Detected
              </h2>
              <button
                onClick={() => setShowConflict(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fa fa-close w-5 h-5"></i>
              </button>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">
                      Time
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {conflictSchedules.map((schedule) => {
                    const startDate = new Date(schedule.startTime);
                    const endDate = new Date(schedule.endTime);

                    return (
                      <tr
                        key={schedule._id}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {schedule.userID.preferredName}
                          <p className="text-xs text-gray-500">
                            {schedule.userID.email}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {schedule.locationID.name}
                          <p className="text-xs text-gray-500">
                            {schedule.locationID.area}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {startDate.toLocaleDateString("en-GB", {
                            weekday: "short",
                            day: "2-digit",
                            month: "short",
                            timeZone: "UTC", // Ensures date is interpreted in UTC
                          })}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {new Intl.DateTimeFormat(undefined, {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                            timeZone: "UTC", // Force UTC+00 output
                          }).format(startDate)}{" "}
                          -{" "}
                          {new Intl.DateTimeFormat(undefined, {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                            timeZone: "UTC",
                          }).format(endDate)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowLoadTemplateModal(false);
                  setShowConflict(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <CopyResultDialog
        open={openCopyResultDialog}
        onClose={() => setOpenCopyResultDialog(false)}
        result={copyResults}
      />
    </div>
  );
}
